if (!customElements.get('leasing-flow')) {
  customElements.define(
    'leasing-flow',
    class LeasingFlow extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
        this.pageScrollLock = null;
        this.handleEscape = this.onEscape.bind(this);
        this.handleSourceChange = this.syncFromSource.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.scope = this.closest('[data-leasing-scope]') || this.parentElement;
        this.productRoot = this.closest('product-info');
        this.form =
          (this.scope && this.scope.closest('form')) ||
          this.closest('form') ||
          (this.productRoot && this.productRoot.querySelector('form[data-type="add-to-cart-form"]')) ||
          (this.productRoot && this.productRoot.querySelector('form[action*="/cart/add"]')) ||
          document.querySelector('product-info form[action*="/cart/add"]');
        this.buySurface = this.scope ? this.scope.querySelector('[data-leasing-buy-surface]') : null;
        const isDirectOpen = this.classList.contains('leasing-flow--direct-open');
        if (!this.scope || (!this.form && !isDirectOpen) || (!this.buySurface && !isDirectOpen)) return;

        this.context = this.dataset.context || 'product-form';
        this.productTitle = this.dataset.productTitle || '';
        this.productId = this.dataset.productId || '';
        this.initialVariantId = this.dataset.initialVariantId || '';
        this.currencyCode = this.dataset.currencyCode || 'CHF';
        this.locale = this.dataset.locale || 'de-CH';
        this.initialPriceCents = this.toInt(this.dataset.initialPriceCents);
        this.maxDownPercent = this.toNumber(this.dataset.maxDownPercent) || 50;
        this.financingApr = this.toNumber(this.dataset.financingApr) || 9.89;
        this.tokenVariantId = this.dataset.tokenVariantId || '';
        this.deliveryVariantId = this.dataset.deliveryVariantId || '';
        this.processingVariantId = this.dataset.processingVariantId || '';
        this.defaultMonths = this.toInt(this.dataset.defaultMonths) || 12;
        this.defaultDown = this.toInt(this.dataset.defaultDown);
        this.planTerms = {
          bnpl: this.parseTerms(this.dataset.bnplTerms, [3, 4, 6, 10, 12]),
          financing: this.parseTerms(this.dataset.financingTerms, [24, 36, 48]),
        };
        this.currentPlan = this.dataset.defaultPlan === 'financing' ? 'financing' : 'bnpl';
        this.externalPriceCents = 0;
        this.externalConfiguration = '';
        this.variants = this.parseJson('[data-leasing-variants]');
        const translations = this.parseJson('[data-leasing-translations]');
        this.translations = translations && !Array.isArray(translations) ? translations : {};
        this.moneyFormatter = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currencyCode });
        this.moneyWholeFormatter = new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });

        this.variantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
        this.totalCentsInput = this.form ? this.form.querySelector('[data-leasing-total-cents]') : null;
        this.configurator = this.closest('[data-stark-configurator]');
        this.modeButtons = Array.from(this.querySelectorAll('[data-leasing-mode]'));
        this.planButtons = Array.from(this.querySelectorAll('[data-leasing-plan]'));
        this.leaseSurface = this.querySelector('[data-leasing-lease-surface]');
        this.openButton = this.querySelector('[data-leasing-open]');
        this.openButtonLabel = this.querySelector('[data-leasing-open-label]');
        this.defaultOpenButtonLabel = this.openButtonLabel ? this.openButtonLabel.textContent.trim() : this.t('open_label', {}, 'Finance calculator');
        this.modal = this.querySelector('[data-leasing-modal]');
        this.modalBody = this.querySelector('.leasing-flow__modal-body');
        this.closeButtons = Array.from(this.querySelectorAll('[data-leasing-close]'));
        this.status = this.querySelector('[data-leasing-status]');
        this.actions = this.querySelector('[data-leasing-actions]');
        this.monthsSelect = this.querySelector('[data-leasing-months-select]');
        this.monthsRange = this.querySelector('[data-leasing-months-range]');
        this.downInput = this.querySelector('[data-leasing-down-input]');
        this.downRange = this.querySelector('[data-leasing-down-range]');
        this.percentButtons = Array.from(this.querySelectorAll('[data-leasing-percent]'));
        this.monthlyAmount = this.querySelector('[data-leasing-monthly-amount]');
        this.summaryNote = this.querySelector('[data-leasing-summary-note]');
        this.downOutput = this.querySelector('[data-leasing-down-output]');
        this.financedOutput = this.querySelector('[data-leasing-financed-output]');
        this.aprOutput = this.querySelector('[data-leasing-apr-output]');
        this.teasers = Array.from(this.querySelectorAll('[data-leasing-teaser]'));
        this.submitButton = this.querySelector('[data-leasing-submit]');
        this.submitLabel = this.querySelector('[data-leasing-submit-label]');
        this.defaultSubmitLabel = this.submitLabel ? this.submitLabel.textContent.trim() : this.t('submit', {}, 'Add financing to cart');
        this.isBusy = false;

        this.bindEvents();
        if (this.downInput) this.downInput.value = String(this.defaultDown);
        this.setPlan(this.currentPlan, false);
        this.syncFromSource();
        if (this.modeButtons.length) this.setMode('buy');
      }

      disconnectedCallback() {
        this.unlockPageScroll();
        this.restoreModalHome();
        if (this.variantInput) this.variantInput.removeEventListener('change', this.handleSourceChange);
        if (this.totalCentsInput) this.totalCentsInput.removeEventListener('change', this.handleSourceChange);
        if (this.configurator) this.configurator.removeEventListener('stark-configurator:updated', this.handleSourceChange);
        document.removeEventListener('keydown', this.handleEscape);
      }

      bindEvents() {
        this.modeButtons.forEach((button) => button.addEventListener('click', () => this.setMode(button.dataset.leasingMode)));
        if (this.openButton) {
          this.openButton.addEventListener('click', () => {
            if (!this.isBusy && !this.openButton.disabled) this.openModal(this.openButton);
          });
        }
        this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeModal()));
        this.planButtons.forEach((button) => button.addEventListener('click', () => this.setPlan(button.dataset.leasingPlan)));
        if (this.monthsSelect) {
          this.monthsSelect.addEventListener('change', () => {
            this.syncMonths(this.toInt(this.monthsSelect.value));
            this.recalculate();
          });
        }
        if (this.monthsRange) {
          this.monthsRange.addEventListener('input', () => {
            const terms = this.getCurrentTerms();
            const index = Math.max(0, Math.min(this.toInt(this.monthsRange.value), terms.length - 1));
            this.syncMonths(terms[index]);
            this.recalculate();
          });
        }
        if (this.downInput) {
          ['input', 'change'].forEach((eventName) => this.downInput.addEventListener(eventName, () => {
            this.syncDownValue(this.toNumber(this.downInput.value));
            this.recalculate();
          }));
        }
        if (this.downRange) {
          ['input', 'change'].forEach((eventName) => this.downRange.addEventListener(eventName, () => {
            this.syncDownValue(this.toNumber(this.downRange.value));
            this.recalculate();
          }));
        }
        this.percentButtons.forEach((button) => button.addEventListener('click', () => {
          const percent = this.toNumber(button.dataset.leasingPercent);
          this.syncDownValue(Math.round((this.getCurrentPriceValue() * percent) / 100));
          this.recalculate();
        }));
        if (this.submitButton) this.submitButton.addEventListener('click', () => this.submitFinancing());
        if (this.variantInput) this.variantInput.addEventListener('change', this.handleSourceChange);
        if (this.totalCentsInput) this.totalCentsInput.addEventListener('change', this.handleSourceChange);
        if (this.configurator) this.configurator.addEventListener('stark-configurator:updated', this.handleSourceChange);
        document.addEventListener('keydown', this.handleEscape);
      }

      onEscape(event) {
        if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.closeModal();
      }

      setMode(mode) {
        const isFinanceMode = mode === 'bnpl' || mode === 'financing';
        this.modeButtons.forEach((button) => {
          const active = button.dataset.leasingMode === mode;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        if (this.buySurface) this.buySurface.hidden = isFinanceMode;
        if (this.leaseSurface) this.leaseSurface.hidden = !isFinanceMode;
        if (isFinanceMode) this.setPlan(mode);
        else this.closeModal();
      }

      setPlan(plan, preserveTerm = true) {
        const preferredMonths = preserveTerm ? this.getSelectedMonths() : this.defaultMonths;
        this.currentPlan = plan === 'financing' ? 'financing' : 'bnpl';
        this.planButtons.forEach((button) => {
          const active = button.dataset.leasingPlan === this.currentPlan;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        this.populateTermOptions(preferredMonths);
        this.recalculate();
      }

      getCurrentTerms() {
        return this.planTerms[this.currentPlan] || this.planTerms.bnpl;
      }

      populateTermOptions(preferredMonths) {
        const terms = this.getCurrentTerms();
        const months = terms.includes(preferredMonths) ? preferredMonths : terms.includes(this.defaultMonths) ? this.defaultMonths : terms[0];
        if (this.monthsSelect) {
          this.monthsSelect.replaceChildren();
          terms.forEach((term) => {
            const option = document.createElement('option');
            option.value = String(term);
            option.textContent = this.t('months_option', { count: String(term) }, `${term} months`);
            option.selected = term === months;
            this.monthsSelect.appendChild(option);
          });
        }
        if (this.monthsRange) {
          this.monthsRange.min = '0';
          this.monthsRange.max = String(Math.max(terms.length - 1, 0));
          this.monthsRange.value = String(Math.max(terms.indexOf(months), 0));
        }
      }

      syncMonths(months) {
        const terms = this.getCurrentTerms();
        const safeMonths = terms.includes(months) ? months : terms[0];
        if (this.monthsSelect) this.monthsSelect.value = String(safeMonths);
        if (this.monthsRange) this.monthsRange.value = String(Math.max(terms.indexOf(safeMonths), 0));
      }

      getSelectedMonths() {
        const terms = this.getCurrentTerms();
        const months = this.toInt(this.monthsSelect ? this.monthsSelect.value : this.defaultMonths);
        return terms.includes(months) ? months : terms[0];
      }

      openModal(trigger) {
        if (!this.modal || !this.modal.hidden) return;
        this.lastTrigger = trigger || this.lastTrigger || null;
        // Reset every new session to the lowest 0% monthly payment: 12 months.
        this.setPlan('bnpl', false);
        this.mountModalAtViewportRoot();
        this.lockPageScroll();
        this.modal.hidden = false;
        this.modal.classList.add('is-open');
        this.clearStatus();
        const focusTarget = this.modal.querySelector('[data-leasing-plan].is-active') || this.modal.querySelector('[data-leasing-close]');
        if (focusTarget) window.setTimeout(() => focusTarget.focus({ preventScroll: true }), 0);
      }

      open({ trigger = null, priceCents = 0, configuration = '' } = {}) {
        this.externalPriceCents = this.toInt(priceCents);
        this.externalConfiguration = String(configuration || '');
        this.syncFromSource();
        this.openModal(trigger);
      }

      closeModal() {
        if (!this.modal || this.isBusy) return;
        const trigger = this.lastTrigger;
        this.modal.hidden = true;
        this.modal.classList.remove('is-open');
        this.unlockPageScroll();
        this.restoreModalHome();
        if (trigger && typeof trigger.focus === 'function') window.setTimeout(() => trigger.focus({ preventScroll: true }), 0);
      }

      lockPageScroll() {
        if (this.pageScrollLock) return;
        const bodyStyle = document.body.style;
        const rootStyle = document.documentElement.style;
        this.pageScrollLock = {
          scrollX: window.scrollX,
          scrollY: window.scrollY,
          body: { overflow: bodyStyle.overflow, position: bodyStyle.position, top: bodyStyle.top, left: bodyStyle.left, right: bodyStyle.right, width: bodyStyle.width },
          rootScrollBehavior: rootStyle.scrollBehavior,
        };
        rootStyle.scrollBehavior = 'auto';
        bodyStyle.overflow = 'hidden';
        bodyStyle.position = 'fixed';
        bodyStyle.top = `-${this.pageScrollLock.scrollY}px`;
        bodyStyle.left = `-${this.pageScrollLock.scrollX}px`;
        bodyStyle.right = '0';
        bodyStyle.width = '100%';
        window.scrollTo(0, 0);
      }

      unlockPageScroll() {
        if (!this.pageScrollLock) return;
        const lock = this.pageScrollLock;
        Object.assign(document.body.style, lock.body);
        document.documentElement.style.scrollBehavior = lock.rootScrollBehavior;
        window.scrollTo(lock.scrollX, lock.scrollY);
        this.pageScrollLock = null;
      }

      mountModalAtViewportRoot() {
        if (!this.modal || this.context !== 'product-form' || this.modal.parentNode === document.body) return;
        const styles = window.getComputedStyle(this);
        ['--leasing-surface', '--leasing-surface-strong', '--leasing-border', '--leasing-border-strong', '--leasing-soft', '--leasing-soft-strong', '--leasing-text', '--leasing-muted', '--leasing-accent', '--leasing-accent-text', '--leasing-overlay', '--leasing-shadow', '--leasing-select-icon'].forEach((property) => {
          this.modal.style.setProperty(property, styles.getPropertyValue(property));
        });
        this.modal.dataset.leasingContext = this.context;
        this.modalPlaceholder = document.createComment('leasing-flow-modal-home');
        this.modal.parentNode.insertBefore(this.modalPlaceholder, this.modal);
        document.body.appendChild(this.modal);
      }

      restoreModalHome() {
        if (!this.modal || !this.modalPlaceholder) return;
        if (this.modalPlaceholder.parentNode) {
          this.modalPlaceholder.parentNode.insertBefore(this.modal, this.modalPlaceholder);
          this.modalPlaceholder.remove();
        }
        this.modalPlaceholder = null;
      }

      getSelectedVariant() {
        const variantId = this.variantInput ? String(this.variantInput.value || '') : String(this.initialVariantId || '');
        if (!variantId || !Array.isArray(this.variants)) return null;
        return this.variants.find((variant) => String(variant.id) === variantId) || null;
      }

      getCurrentPriceCents() {
        if (this.externalPriceCents > 0) return this.externalPriceCents;
        if (this.context === 'stark-configurator' && this.totalCentsInput) {
          const total = this.toInt(this.totalCentsInput.value);
          if (total > 0) return total;
        }
        const variant = this.getSelectedVariant();
        return variant && typeof variant.price === 'number' ? variant.price : this.initialPriceCents;
      }

      getCurrentPriceValue() {
        return this.getCurrentPriceCents() / 100;
      }

      getCurrentPreviewImageUrl(variant) {
        if (this.context === 'stark-configurator' && this.configurator) {
          const color = this.configurator.querySelector('[data-config-option][data-step="color"].is-selected');
          const source = color ? color.dataset.image || color.dataset.stageImage || '' : '';
          if (source) return source;
        }
        if (variant && variant.featured_image && variant.featured_image.src) return String(variant.featured_image.src);
        if (variant && variant.image && variant.image.src) return String(variant.image.src);
        if (variant && variant.featured_media && variant.featured_media.preview_image) return String(variant.featured_media.preview_image.src || '');
        return '';
      }

      getMaxDownValue() {
        return Math.max(0, Math.round((this.getCurrentPriceValue() * this.maxDownPercent) / 100));
      }

      syncDownBounds() {
        const maxDown = this.getMaxDownValue();
        const current = this.downInput ? this.toNumber(this.downInput.value) : 0;
        const safeValue = Math.min(Math.max(current, 0), maxDown);
        if (this.downInput) {
          this.downInput.max = String(maxDown);
          this.downInput.value = String(safeValue);
        }
        if (this.downRange) {
          this.downRange.max = String(maxDown);
          this.downRange.value = String(safeValue);
        }
      }

      syncDownValue(value) {
        const safeValue = Math.min(Math.max(this.toNumber(value), 0), this.getMaxDownValue());
        if (this.downInput) this.downInput.value = String(safeValue);
        if (this.downRange) this.downRange.value = String(safeValue);
      }

      getPlan() {
        const months = this.getSelectedMonths();
        const price = this.getCurrentPriceValue();
        const down = Math.min(this.getMaxDownValue(), Math.max(0, this.toNumber(this.downInput ? this.downInput.value : 0)));
        const financed = Math.max(0, price - down);
        const apr = this.currentPlan === 'financing' ? this.financingApr : 0;
        let monthly = financed / Math.max(months, 1);
        if (apr > 0 && financed > 0) {
          const monthlyRate = Math.pow(1 + apr / 100, 1 / 12) - 1;
          monthly = (financed * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
        }
        return { type: this.currentPlan, months, price, down, financed, monthly, apr, financedRounded: Math.round(financed) };
      }

      recalculate() {
        const plan = this.getPlan();
        const priceAvailable = plan.price > 0;
        if (this.monthlyAmount) this.monthlyAmount.textContent = priceAvailable ? this.formatMoney(plan.monthly, true) : this.t('price_pending', {}, 'Price pending');
        if (this.downOutput) this.downOutput.textContent = this.formatMoney(plan.down, true);
        if (this.financedOutput) this.financedOutput.textContent = priceAvailable ? this.formatMoney(plan.financed) : this.t('price_pending', {}, 'Price pending');
        if (this.aprOutput) this.aprOutput.textContent = plan.apr > 0 ? `${plan.apr.toFixed(2)}%` : '0%';
        if (this.summaryNote) {
          const key = plan.type === 'financing' ? 'summary_financing' : 'summary_bnpl';
          this.summaryNote.textContent = this.t(key, { months: String(plan.months), down: this.formatMoney(plan.down, true), apr: plan.apr.toFixed(2) }, '');
        }
        this.teasers.forEach((teaser) => {
          if (!priceAvailable) teaser.textContent = this.t('teaser_price_pending', {}, 'Financing becomes available once a product price is set.');
          else if (plan.financedRounded > 0) teaser.textContent = this.t('teaser_estimate', { amount: this.formatMoney(plan.monthly, true), months: String(plan.months) }, 'Estimated from {{ amount }} / month over {{ months }} months.');
          else teaser.textContent = this.t('teaser_down_payment', {}, 'Choose a lower down payment to create a financing plan.');
        });
        const percent = plan.price > 0 ? Math.round((plan.down / plan.price) * 100) : 0;
        this.percentButtons.forEach((button) => button.classList.toggle('is-active', this.toInt(button.dataset.leasingPercent) === percent));
      }

      syncFromSource() {
        this.syncDownBounds();
        this.recalculate();
        const variant = this.getSelectedVariant();
        const priceAvailable = this.getCurrentPriceValue() > 0;
        const available = this.context === 'stark-configurator' ? !variant || variant.available !== false : !!variant && variant.available !== false;
        if (this.openButton) {
          this.openButton.disabled = (priceAvailable && !available) || this.isBusy;
          this.openButton.setAttribute('aria-disabled', String(this.openButton.disabled));
        }
        if (this.submitButton) this.submitButton.disabled = !priceAvailable || !available || this.isBusy;
        this.percentButtons.forEach((button) => { button.disabled = !priceAvailable || this.isBusy; });
        this.classList.toggle('is-price-pending', !priceAvailable);
        if (this.openButtonLabel) this.openButtonLabel.textContent = this.defaultOpenButtonLabel;
      }

      buildExistingProperties() {
        const properties = {};
        if (!this.form) return properties;
        Array.from(this.form.querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]')).forEach((input) => {
          const match = input.name.match(/^properties\[(.*)\]$/);
          const key = match && match[1] ? match[1] : '';
          if (!key || key.charAt(0) === '_' || input.value === '' || ((input.type === 'checkbox' || input.type === 'radio') && !input.checked)) return;
          properties[key] = input.value;
        });
        return properties;
      }

      buildFinancingProperties(plan, variant) {
        const planName = plan.type === 'financing' ? this.t('plan_financing', {}, 'Financing') : this.t('plan_bnpl', {}, '0% financing');
        return Object.assign({}, this.buildExistingProperties(), {
          '_Leasing Marker': this.tokenVariantId ? 'token' : 'product',
          '_Leasing Provider': 'HeyLight',
          '_Leasing Product URL': window.location.pathname || '',
          '_Leasing Preview Image': this.getCurrentPreviewImageUrl(variant),
          '_Leasing Product ID': this.productId,
          '_Leasing Variant ID': variant && variant.id ? String(variant.id) : '',
          'Leasing: Product': this.productTitle,
          'Leasing: Configuration': this.externalConfiguration,
          'Leasing: Variant': variant && variant.title ? String(variant.title) : '',
          'Leasing: Plan': planName,
          'Leasing: Effective APR': plan.apr > 0 ? `${plan.apr.toFixed(2)}%` : '0%',
          'Leasing: Term (months)': String(plan.months),
          'Leasing: Down payment (CHF)': this.formatMoney(plan.down),
          'Leasing: Product price (CHF)': this.formatMoney(plan.price),
          'Leasing: Financed (CHF)': this.formatMoney(plan.financed),
          'Leasing: Monthly (CHF)': this.formatMoney(plan.monthly, true),
        });
      }

      buildFeeItems() {
        const items = [];
        if (this.deliveryVariantId) items.push({ id: Number(this.deliveryVariantId), quantity: 1, properties: { '_Leasing Marker': 'fee', 'Leasing: Product': this.productTitle, 'Leasing: Fee': 'Delivery' } });
        if (this.processingVariantId) items.push({ id: Number(this.processingVariantId), quantity: 1, properties: { '_Leasing Marker': 'fee', 'Leasing: Product': this.productTitle, 'Leasing: Fee': 'Processing' } });
        return items.filter((item) => Number.isFinite(item.id) && item.id > 0);
      }

      async submitFinancing() {
        if (this.isBusy) return;
        const variant = this.getSelectedVariant();
        if (this.context !== 'stark-configurator' && (!variant || variant.available === false)) {
          this.setStatus(this.t('error_select_variant', {}, 'Select an available variant before continuing.'), 'error');
          return;
        }
        const plan = this.getPlan();
        if (plan.financedRounded < 1) {
          this.setStatus(this.t('error_financed_amount', {}, 'The financed amount must remain above CHF 0.'), 'error');
          return;
        }
        if (plan.down > 0) {
          this.setStatus(this.t('error_deposit_checkout_unsupported', {}, 'A down payment is not currently supported in HeyLight checkout. Choose CHF 0 to continue; other values are estimates only.'), 'error');
          return;
        }
        if (!this.tokenVariantId) {
          this.setStatus(this.t('error_token_missing', {}, 'The financing token variant is not configured.'), 'error');
          return;
        }
        this.setBusy(true, this.t('busy_adding_to_cart', {}, 'Adding financing to cart...'));
        try {
          const cleanupIds = [this.tokenVariantId, this.deliveryVariantId, this.processingVariantId].filter(Boolean);
          await this.cleanupCartBeforeAdd(cleanupIds);
          const items = [{
            id: Number(this.tokenVariantId),
            quantity: plan.financedRounded,
            properties: this.buildFinancingProperties(plan, variant),
          }, ...this.buildFeeItems()];
          await this.addItems(items);
          window.location.href = '/cart';
        } catch (error) {
          this.setBusy(false);
          this.setStatus(error && error.message ? error.message : this.t('error_create_request', {}, 'Unable to create the financing request.'), 'error');
        }
      }

      async cleanupCartBeforeAdd(variantIds) {
        const response = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!response.ok) return;
        const cart = await response.json();
        const updates = {};
        const ids = new Set((variantIds || []).map(String));
        (cart.items || []).forEach((item) => {
          const marker = item && item.properties ? item.properties['_Leasing Marker'] || item.properties['Leasing: Marker'] : '';
          if (marker || ids.has(String(item.variant_id))) updates[item.key] = 0;
        });
        if (!Object.keys(updates).length) return;
        const update = await fetch('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ updates }),
        });
        if (!update.ok) throw new Error(this.t('error_clear_existing', {}, 'Unable to clear the existing financing request from the cart.'));
      }

      async addItems(items) {
        const response = await fetch('/cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || (payload && payload.status)) {
          throw new Error((payload && (payload.description || payload.message)) || this.t('error_add_request', {}, 'Unable to add the financing request to the cart.'));
        }
        return payload;
      }

      setBusy(isBusy, message) {
        this.isBusy = isBusy;
        if (this.submitButton) {
          this.submitButton.disabled = isBusy;
          this.submitButton.classList.toggle('is-busy', isBusy);
          this.submitButton.setAttribute('aria-busy', String(isBusy));
        }
        if (this.actions) this.actions.classList.toggle('is-busy', isBusy);
        if (this.submitLabel) this.submitLabel.textContent = isBusy ? this.t('submit_busy', {}, 'Preparing financing...') : this.defaultSubmitLabel;
        if (message) this.setStatus(message, 'info');
        else if (!isBusy) this.clearStatus();
        this.syncFromSource();
      }

      setStatus(message, state) {
        if (!this.status) return;
        this.status.hidden = !message;
        this.status.dataset.state = state || 'info';
        this.status.textContent = message || '';
      }

      clearStatus() {
        if (!this.status) return;
        this.status.hidden = true;
        this.status.textContent = '';
        delete this.status.dataset.state;
      }

      t(key, replacements, fallback) {
        const template = typeof this.translations[key] === 'string' ? this.translations[key] : fallback || '';
        return String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => Object.prototype.hasOwnProperty.call(replacements || {}, token) ? String(replacements[token]) : '');
      }

      parseJson(selector) {
        const node = this.querySelector(selector);
        if (!node) return [];
        try { return JSON.parse(node.textContent); } catch (error) { return []; }
      }

      parseTerms(value, fallback) {
        const terms = String(value || '').split(',').map((item) => this.toInt(item)).filter((item) => item > 0);
        return terms.length ? Array.from(new Set(terms)) : fallback;
      }

      formatMoney(value, whole = false) {
        return (whole ? this.moneyWholeFormatter : this.moneyFormatter).format(this.toNumber(value));
      }

      toInt(value) {
        const number = parseInt(value, 10);
        return Number.isFinite(number) ? number : 0;
      }

      toNumber(value) {
        const number = Number(value);
        return Number.isFinite(number) ? number : 0;
      }
    }
  );
}
